import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Tag as TagIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { TagPill, autoTagColor, tagTint } from './TagPill';

export interface PickerTag {
  id: number;
  name: string;
  color: string;
}

interface Props {
  /** El vocabulario disponible para este tipo de entidad. */
  vocabulario: PickerTag[];
  /** Las que lleva puestas ahora la entidad. */
  valor: PickerTag[];
  onChange: (tags: PickerTag[]) => void;
  /** Crear una nueva. Devuelve la creada (o la que ya había con ese nombre). */
  onCrear: (name: string, color: string) => Promise<PickerTag>;
  disabled?: boolean;
  className?: string;
}

/**
 * Poner y quitar etiquetas a una entidad.
 *
 * Un solo campo hace las dos cosas que pediste —elegir de lo ya creado y crear
 * algo nuevo— porque en la cabeza de quien etiqueta son la misma: escribe lo
 * que quiere marcar y, si existe, lo reutiliza. Separarlo en «elegir» y «crear»
 * obliga a saber de antemano cuál de las dos va a ser.
 *
 * **Escribir filtra; crear es explícito.** Al teclear se acota la lista, y sólo
 * cuando lo escrito no coincide con nada aparece «crear "…"». Así no se
 * fabrican etiquetas por accidente al pulsar Enter demasiado rápido, que es
 * como acaban los vocabularios llenos de duplicados con una letra de
 * diferencia.
 */
export function TagPicker({
  vocabulario,
  valor,
  onChange,
  onCrear,
  disabled,
  className,
}: Props) {
  const t = useT();
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [creando, setCreando] = useState(false);
  const caja = useRef<HTMLDivElement>(null);

  // Cerrar al pulsar fuera. Sin esto el panel se queda abierto tapando la fila
  // siguiente mientras el usuario ya está en otra cosa.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const puestas = useMemo(() => new Set(valor.map((v) => v.id)), [valor]);
  const consulta = texto.trim().toLowerCase();

  const candidatas = useMemo(
    () =>
      vocabulario
        .filter((v) => !puestas.has(v.id))
        .filter((v) => !consulta || v.name.toLowerCase().includes(consulta)),
    [vocabulario, puestas, consulta],
  );

  // Sólo se ofrece crear si lo escrito no existe YA, mirando todo el
  // vocabulario y no sólo lo que queda por poner: si la etiqueta ya está
  // puesta, ofrecer «crear» sería mentir.
  const yaExiste = vocabulario.some((v) => v.name.toLowerCase() === consulta);
  const puedeCrear = consulta.length > 0 && !yaExiste;

  const poner = (tag: PickerTag) => {
    onChange([...valor, tag].sort((a, b) => a.name.localeCompare(b.name)));
    setTexto('');
  };

  const crear = async () => {
    if (!puedeCrear || creando) return;
    setCreando(true);
    try {
      // El color sale del tamaño del vocabulario, girando el matiz por el
      // ángulo áureo: dos etiquetas creadas seguidas salen claramente
      // distintas y la serie no se agrupa en una zona de la rueda. Al azar
      // saldrían parecidas de vez en cuando, que es cuando el color deja de
      // servir para distinguirlas.
      const color = autoTagColor(vocabulario.length);
      const nueva = await onCrear(texto.trim(), color);
      poner(nueva);
    } finally {
      setCreando(false);
    }
  };

  return (
    <div ref={caja} className={cn('relative', className)}>
      <div className="flex flex-wrap items-center gap-1.5">
        {valor.map((tag) => (
          <TagPill
            key={tag.id}
            name={tag.name}
            color={tag.color}
            onRemove={
              disabled ? undefined : () => onChange(valor.filter((v) => v.id !== tag.id))
            }
          />
        ))}

        {!disabled && (
          <button
            type="button"
            onClick={() => setAbierto((v) => !v)}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-[var(--color-border-default)] px-2.5 py-1 text-[11px] font-semibold text-[var(--color-fg-muted)] transition-colors hover:border-[var(--color-accent-ink)] hover:text-[var(--color-accent-ink)]"
          >
            <TagIcon className="h-3 w-3" />
            {valor.length === 0 ? t('tags.picker.add') : t('tags.picker.more')}
          </button>
        )}
      </div>

      {abierto && !disabled && (
        <div className="absolute left-0 top-full z-30 mt-1.5 w-64 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-bg-elevated)] p-2 shadow-lg">
          <input
            autoFocus
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                // Enter pone la primera coincidencia; sólo crea cuando no hay
                // ninguna. Al revés se fabricarían duplicados sin querer.
                if (candidatas.length) poner(candidatas[0]);
                else void crear();
              }
              if (e.key === 'Escape') setAbierto(false);
            }}
            placeholder={t('tags.picker.placeholder')}
            maxLength={40}
            className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-2.5 py-1.5 text-xs text-[var(--color-fg-primary)] placeholder:text-[var(--color-fg-subtle)] focus:border-[var(--color-accent-ink)] focus:outline-none"
          />

          <div className="mt-1.5 max-h-52 space-y-0.5 overflow-y-auto scrollbar-thin">
            {candidatas.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => poner(tag)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-[var(--color-fg-primary)] hover:bg-[var(--color-bg-surface)]"
              >
                <span
                  aria-hidden
                  className="h-2 w-2 flex-none rounded-full"
                  style={{ background: tagTint(tag.color) }}
                />
                <span className="truncate">{tag.name}</span>
              </button>
            ))}

            {candidatas.length === 0 && !puedeCrear && (
              <p className="px-2 py-2 text-[11px] text-[var(--color-fg-muted)]">
                {vocabulario.length === 0
                  ? t('tags.picker.emptyVocab')
                  : t('tags.picker.noMatches')}
              </p>
            )}

            {puedeCrear && (
              <button
                type="button"
                onClick={() => void crear()}
                disabled={creando}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs font-semibold text-[var(--color-accent-ink)] hover:bg-[var(--color-bg-surface)] disabled:opacity-50"
              >
                <Plus className="h-3 w-3 flex-none" />
                <span className="truncate">
                  {t('tags.picker.create', { name: texto.trim() })}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
